import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, SimpleChanges, OnInit, OnChanges, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../../shared/models/country';
import type { EntityCountsResponse, EntityList } from '../../../../shared/models/entity.models';
import type { Language } from '../../../../shared/models/language';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { AddProtocolUserComponent } from './add-user/add-user.component';
import { ListResponse } from '../../../../shared/models/listResponse.models';

@Component({
  selector: 'app-protocol-users',
  templateUrl: './protocol-users.component.html',
  styleUrl: './protocol-users.component.scss',
  standalone: true,
  imports: [TabViewModule, TranslateModule, MITableComponent, BreadcrumbModule,
    ButtonModule, DialogModule, AccessProviderDirective, AddProtocolUserComponent]
})
export class ProtocolUsersComponent implements OnInit, OnChanges {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @ViewChild('addUser') addUser: AddProtocolUserComponent;
  @Input() isActive: boolean = false;
  @Input() isSiteReady: boolean = false;
  @Input() siteEntityId: string;
  @Input() isEditNotAllowed: boolean = false;
  @Input() isEditNotAllowedSpecificStatus: boolean = false;
  @Input() isChangeReasonNotRequired: boolean = false;
  @Input() isAllowToAddProduct: boolean = false;
  @Input() attributeValues: any;
  @Input() protocolId: string;
  @Output() saveSuccess = new EventEmitter<void>();

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns = [];
  cols = [];
  moduleName = AppSettings.ENTITY_CODE.PROTOCOL_USER;
  filterList;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels = {};
  filterAttributeLabels = {};
  listColumns: any;
  allColumns: any;
  pagination: any;
  totalRecords: number = 0;
  tableData: any = [];
  buttons: any[] = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number = AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteSiteMsg: string;
  deleteSiteHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: any[];
  isReady: boolean = false;
  attributeData: any;
  data;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] = [];
  currentRow;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader;
  reviewHeader: any;
  btnUpdate: any;
  bulkUpdateLabel: string = this.cs.getLabel('protocol_user.lbl_update_user');
  bulkDeleteLabel: string = this.cs.getLabel('protocol_user.lbl_delete_user');
  entityCreatedAtAttributeId;
  regionGeofences: any = [];
  zoneIds: any = [];
  isEntity = true;
  showAddDialog: boolean = false;
  userId: string;
  sponsorDetails: any;
  userStatusAttributeId: string;
  protocolAttributeId: any;

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private messageService: MessageService,
    private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService,
    private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef,
    private loaderService: LoaderService
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeRowItems();
    this.initializeModuleHeaderSecondaryBtnMenuItems();
    this.initializeOtherVariablesAndAPI();
    if (this.isAllowToAddProduct) {
      this.isAllowToAddProduct = this.permissionAccessService.hasAccess('PROTOCOL_USERS', '', 'ADD', '');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive && changes.isActive.currentValue === true && !this.data) {
      this.initializeLabels();
      this.initializeRowItems();
      this.initializeModuleHeaderSecondaryBtnMenuItems();
      this.initializeOtherVariablesAndAPI();
      this.getMetaData();
    }
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {
      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setUserFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        // this.entityData.defaultSortColumn = this.entityCreatedAtAttributeId;
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setUserTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort);
    });
  }

  setUserFilterView(filterResponse) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse);
    this.filterAttributeLabels['protocol_user.fields.protocol_user_type.label'] = this.cs.getLabel('manage_users.columns.lbl_user_type');
    this.filterList = this.cs.getColumns(filterResponse);
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
    this.filterList = this.filterList.map(ele => {
      if (ele.attributeCode === 'can_order') {
        ele.presetValues = [
          {
            labelKey: true,
            labelValue: 'Yes'
          },
          {
            labelKey: false,
            labelValue: 'No'
          }
        ];
      }
      if (ele.attributeCode === 'user_parent') {
        ele.inputCode = 'select';
        ele.multi = true;
        this.setParentOptionFilter().then(presetValues => {
          ele.presetValues = presetValues;
        });
      }
      return ele;
    });
    this.protocolAttributeId = this.filterList.find(field => field.attributeCode === 'protocol_entity_id')?.attributeId;
    this.filterList = this.filterList.filter(filter => filter.attributeCode !== 'protocol_entity_id');
    this.entityData.filters = [{
      attributeId: this.protocolAttributeId,
      attributeValue: this.protocolId
    }]
  }

  setParentOptionFilter(): Promise<any[]> {
    return new Promise((resolve) => {
      const presetValues = [];

      this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL_SITE, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe(filterResponse => {
        if (filterResponse) {
          const protocolEntityIdField = filterResponse.tabs[0].groups[0].fields.find(element => element.attributeCode === 'protocol_entity_id');
          if (protocolEntityIdField) {
            const protocolSiteAttributeId = protocolEntityIdField.attributeId;

            const siteSearchParams = {
              limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
              offset: 0,
              searchStr: "",
              filters: [{
                attributeId: protocolSiteAttributeId,
                attributeValue: this.protocolId
              }],
              countryCode: AppSettings.HEADER_COUNTRY_CODE,
              deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
              forTenantCode: this.configService.getForTenantCode(),
              actionStatus: ''
            };

            this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_SITE, siteSearchParams, false)
              .subscribe({
                next: (res: ListResponse) => {
                  if (res?.data?.length) {
                    res.data.forEach(site => {
                      presetValues.push({
                        labelKey: site.values.site_entity_id,
                        labelValue: site.values?.site_name
                      });
                    });
                  }

                  if (this.attributeValues?.sponsor_details) {
                    const sponsorDetails = this.attributeValues.sponsor_details;
                    presetValues.push({
                      labelKey: sponsorDetails.entityId,
                      labelValue: sponsorDetails.sponsor_name
                    });
                  }

                  resolve(presetValues);
                },
                error: () => {
                  if (this.attributeValues?.sponsor_details) {
                    const sponsorDetails = this.attributeValues.sponsor_details;
                    presetValues.push({
                      labelKey: sponsorDetails.entityId,
                      labelValue: sponsorDetails.sponsor_name
                    });
                  }

                  resolve(presetValues);
                }
              });
          } else {
            if (this.attributeValues?.sponsor_details) {
              const sponsorDetails = this.attributeValues.sponsor_details;
              presetValues.push({
                labelKey: sponsorDetails.entityId,
                labelValue: sponsorDetails.sponsor_name
              });
            }
            resolve(presetValues);
          }
        } else {
          if (this.attributeValues?.sponsor_details) {
            const sponsorDetails = this.attributeValues.sponsor_details;
            presetValues.push({
              labelKey: sponsorDetails.entityId,
              labelValue: sponsorDetails.sponsor_name
            });
          }
          resolve(presetValues);
        }
      });
    });
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_USER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_USER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_USER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      }
    ]
  }

  onAdd() {
    if (this.attributeValues.sponsor_details) {
      this.sponsorDetails = this.attributeValues.sponsor_details;
    }
    this.userId = '';
    this.openAddDialog();
  }

  private initializeRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('PROTOCOL_USERS', '', 'EDIT', '');
    const isActivate = this.permissionAccessService.hasAccess('PROTOCOL_USERS', '', 'ACTIVATE', '');
    const isDelete = this.permissionAccessService.hasAccess('PROTOCOL_USERS', '', 'DELETE', '');
    this.rowItems = [
      ...(isEdit ? [{
        label: this.cs.getLabel('manage_users.edit'),
        icon: `${AppIcons.EDIT_2} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'edit',
        command: () => {
          this.update();
        }
      }] : []),
      { separator: true },
      ...(isActivate ? [{
        label: this.cs.getLabel('protocol.lbl_activate'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Active',
        command: () => {
          this.onUpdateStatus('Active', this.cs.getLabel('protocol.lbl_activate'));
        }
      }] : []),
      ...(isActivate ? [{
        label: this.cs.getLabel('protocol.lbl_deactivate'),
        icon: `${AppIcons.ACTIVATE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Inactive',
        command: () => {
          this.onUpdateStatus('Inactive', this.cs.getLabel('protocol.lbl_deactivate'));
        }
      }] : []),
      { separator: true },
      ...(isDelete ? [{
        label: this.cs.getLabel('lbl_delete'),
        icon: `${AppIcons.BASIC_TRAY} wh-16`,
        iconClass: 'bulk-update-icon',
        command: () => {
          this.tableRef.delete(AppSettings.DELETE_TYPE.ROW);
        }
      }] : []),
    ];
  }

  onUpdateStatus(status, acceptLabelText) {
    const isActive = status === 'Active';

    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'protocol_user.lbl_activate_confirmation' : 'protocol_user.lbl_deactivate_confirmation'),
      message: this.cs.getLabel(isActive ? 'protocol_user.msg_activate_confirmation' : 'protocol_user.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const userData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.userStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.entityService.saveAttributeData(userData.entityCode, this.currentRow.id, userData).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('protocol_user.lbl_activate_successfully'),
            });
            setTimeout(() => {
              this.searchEntity();
              this.getEntityCount();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('protocol_user.lbl_failed_status')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.routePath = [{ label: "Protocol Users", routerLink: 'app/protocol-users' }];
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('Add Protocol User');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteSiteMsg = this.cs.getLabel('protocol_user.confirm_delete_msg');
    this.deleteSiteHeaderMsg = this.cs.getLabel('protocol_user.confirm_delete_header');
  }

  setUserTableView(res) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(this.data);
    this.userStatusAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'user_status')?.attributeId;
    this.allColumns = this.cs.getColumns(this.data).filter(column =>
      !['site_entity_id', 'sponsor_entity_id'].includes(column.attributeCode)
    );
    this.searchEntity();
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `protocol_user.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  searchEntity() {
    try {
      this.loaderService.show();
      this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
      this.tableData = [];
      const isProtocolIdFilter = this.entityData.filters.some(ele => ele.attributeId === this.protocolAttributeId);
      if (!isProtocolIdFilter) {
        this.entityData.filters.push({
          attributeId: this.protocolAttributeId,
          attributeValue: this.protocolId
        });
      }

      this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_USER, this.entityData, false)
        .subscribe({
          next: (res: ListResponse) => {
            if (!res?.data?.length) {
              this.tableData = [];
              this.totalRecords = 0;
              return;
            }
            this.listColumns = res.data[0]?.values || {};
            this.pagination = res.pagination;
            this.totalRecords = res.count || 0;
            this.tableData = [];
            res.data?.forEach(data => {
              const parentArr = data.relatedData?.filter(ele => ele.id === data.values?.user_parent);
              let parentName;
              if (parentArr?.length) {
                parentName = parentArr[0].entityCode === 'site' ? parentArr[0].values?.site_name : parentArr[0].values?.sponsor_name;
              }
              this.tableData.push({
                id: data.id,
                parentName,
                ...data.values,
              });
            });
            this.isSiteReady = true;
          },
          error: () => {
            this.tableData = [];
            this.totalRecords = 0;
          },
          complete: () => {
            this.loaderService.hide();
          }
        });
    } catch (error) {
      this.loaderService.hide();
      this.tableData = [];
      this.totalRecords = 0;
    }
  }

  onSortData(sortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onFilterValueChange(event) {
    const vData = event;
    this.entityData.filters = [];
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  getEntityCount() {
    const res = {
      "tenantId": "2",
      "entityCode": "protocol_user",
      "counts": {
        "UNASSIGNED": 48,
        "ALL": 84,
        "ASSIGNED": 27,
        "DELETED": 9
      }
    }

    if (res?.counts) {
      res.counts.ALL = res.counts.ALL - res.counts.DELETED;
    }
    this.entityCount = res;
  }

  setCurrentRowData(event) {
    this.currentRow = event;
    if (event.deleted) {
      this.rowItems.forEach(item => {
        if (!item.separator) {
          item.disabled = true;
        }
      });
      return;
    }

    this.rowItems.forEach(item => {
      switch (item.value) {
        case 'edit':
          item.disabled = this.isEditNotAllowed || (event.user_status as { Rejected: string })?.Rejected === 'Rejected';
          break;
        case 'Active':
          item.disabled = event.user_status?.Active === 'Active' || event.user_status?.Requested === 'Requested' || this.isEditNotAllowed || (event.user_status as { Rejected: string })?.Rejected === 'Rejected' || (event.user_status as { Archive: string })?.Archive === 'Archived';
          break;
        case 'Inactive':
          item.disabled = event.user_status?.Inactive === 'Inactive' || this.isEditNotAllowed || (event.user_status as { Rejected: string })?.Rejected === 'Rejected' || event.user_status?.Requested === 'Requested' || (event.user_status as { Archive: string })?.Archive === 'Archived';
          break;
      }
    });
  }

  update() {
    this.onUpdateUser(this.currentRow.id);
  }

  onUpdateUser(userId) {
    if (this.attributeValues.sponsor_details) {
      this.sponsorDetails = this.attributeValues.sponsor_details;
    }
    this.userId = userId;
    this.showAddDialog = true;
  }

  openAddDialog() {
    this.showAddDialog = true;
  }

  onSaveSuccess() {
    this.showAddDialog = false;
    this.searchEntity();
    this.saveSuccess.emit();
  }

  private initializeModuleHeaderSecondaryBtnMenuItems() {
    // Initialize secondary button menu items if needed
  }

  onClosePopup(event) {
    if (event) {
      this.searchEntity();
    }
    this.userId = null;
    this.showAddDialog = false;
  }

  onBulkDataUpdateEvent() {
  }

  onBulkDataDeleteEvent(event: string[]) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL_USER,
      entityIds: event
    };
    this.entityService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.cs.getLabel('protocol_user.lbl_bulk_deleted_successfully'),
        });
        setTimeout(() => {
          this.searchEntity();
        }, 300);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.cs.getLabel('protocol_user.lbl_bulk_delete_failed'),
        });
      }
    });
  }

  onUserDelete(event) {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: country[0].countryCode,
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL_USER,
      entityIds: [
        event
      ]
    };
    this.entityService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'protocol_user', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'protocol_user', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }
}


